import { apiClient } from './client';
import { ApiResponse, CostCatalogItem, CostSuggestion, ReportCostSummary } from '@/types';

export const costCatalogApi = {
  // Get catalog items, optionally filtered by category, with regional multiplier
  getCatalog: async (category?: string, zip?: string) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (zip) params.append('zip', zip);
    return apiClient.get<ApiResponse<CostCatalogItem[]>>(`/cost-catalog?${params.toString()}`);
  },

  // Get unique categories
  getCategories: async () => {
    return apiClient.get<ApiResponse<string[]>>('/cost-catalog/categories');
  },

  // Get auto-suggestions for cost items
  getSuggestions: async (query: string, zip?: string) => {
    const params = new URLSearchParams({ q: query });
    if (zip) params.append('zip', zip);
    return apiClient.get<ApiResponse<CostSuggestion[]>>(`/cost-catalog/suggestions?${params.toString()}`);
  },

  // Ask Claude for a low/high cost range for a single finding. The
  // backend caches results on the inspector's deficiency_entries by
  // title + zip3 so re-asking for a familiar finding is free.
  aiSuggest: async (input: {
    title: string;
    description?: string | null;
    severity?: 'safety_concern' | 'recommendation' | null;
    sectionName?: string | null;
    propertyZip?: string | null;
  }) => {
    return apiClient.post<ApiResponse<{
      costLow: number;
      costHigh: number;
      source: 'cache' | 'ai';
      cachedAt: string | null;
    }>>('/cost-catalog/ai-suggest', input);
  },

  // Get report cost summary
  getReportSummary: async (reportId: string) => {
    return apiClient.get<ApiResponse<ReportCostSummary>>(`/cost-catalog/report-summary/${reportId}`);
  },

  // Create inspector override
  createOverride: async (data: {
    catalogItemId?: string;
    category: string;
    itemName: string;
    costLow: number;
    costHigh: number;
    unit?: string;
    notes?: string;
  }) => {
    return apiClient.post<ApiResponse<any>>('/cost-catalog/overrides', data);
  },

  // Update inspector override
  updateOverride: async (id: string, data: Partial<{
    category: string;
    itemName: string;
    costLow: number;
    costHigh: number;
    unit: string;
    notes: string;
  }>) => {
    return apiClient.put<ApiResponse<any>>(`/cost-catalog/overrides/${id}`, data);
  },

  // Delete inspector override
  deleteOverride: async (id: string) => {
    return apiClient.delete<ApiResponse<void>>(`/cost-catalog/overrides/${id}`);
  },

  // Get inspector's overrides
  getOverrides: async () => {
    return apiClient.get<ApiResponse<any[]>>('/cost-catalog/overrides');
  },
};
